import * as React from "react";
import { Building2, Check, ChevronsUpDown, Search } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBranch } from "@/context/branch-context";
import { useIdentity } from "@/context/identity-context";
import { useLocalization } from "@/context/localization-context";

export function BranchSwitcher() {
  const { isMobile } = useSidebar();
  const { identity } = useIdentity();
  const { t, tp } = useLocalization();
  const {
    branches,
    selectedBranch,
    selectedBranches,
    selectedBranchIds,
    branchUsageCounts,
    branchesLoading,
    canSelectBranches,
    selectBranches,
  } = useBranch();

  const [searchQuery, setSearchQuery] = React.useState("");
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const filteredBranches = React.useMemo(() => {
    if (!branches) {
      return [];
    }

    const sortedBranches = [...branches].sort((a, b) => {
      const selectedDelta =
        Number(selectedBranchIds.includes(b.id)) -
        Number(selectedBranchIds.includes(a.id));
      if (selectedDelta !== 0) return selectedDelta;

      const usageDelta =
        (branchUsageCounts[b.id] ?? 0) - (branchUsageCounts[a.id] ?? 0);
      if (usageDelta !== 0) return usageDelta;

      return a.name.localeCompare(b.name);
    });

    if (!searchQuery.trim()) {
      return sortedBranches;
    }

    const query = searchQuery.toLowerCase().trim();
    return sortedBranches.filter(
      (branch) =>
        branch.name.toLowerCase().includes(query) ||
        branch.id.toLowerCase().includes(query) ||
        branch.bb_branch_id?.toLowerCase().includes(query),
    );
  }, [branchUsageCounts, branches, searchQuery, selectedBranchIds]);

  React.useEffect(() => {
    if (!isDropdownOpen) {
      setSearchQuery("");
      setSelectedIndex(-1);
    }
  }, [isDropdownOpen]);

  const selectedCount = selectedBranchIds.length;
  const summaryTitle =
    selectedCount === 0
      ? t("shell.branchSwitcher.allBranches")
      : selectedCount === 1
        ? selectedBranches[0]?.name || tp("count.branches", 1)
        : tp("count.branches", selectedCount);
  const summaryDescription =
    selectedCount === 0
      ? t("shell.branchSwitcher.noRestriction")
      : selectedCount === 1
        ? t("shell.branchSwitcher.selectedBranch")
        : t("shell.branchSwitcher.selectedBranches");

  const toggleBranch = (branchId: string) => {
    if (selectedBranchIds.includes(branchId)) {
      selectBranches(selectedBranchIds.filter((id) => id !== branchId));
      return;
    }

    selectBranches([...selectedBranchIds, branchId]);
  };

  if (!identity) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Building2 className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{t("shell.branchSwitcher.signInRequired")}</span>
              <span className="truncate text-xs">{t("shell.branchSwitcher.noBranchSelected")}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (branchesLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Building2 className="size-4 animate-pulse" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{t("common.loading")}</span>
              <span className="truncate text-xs">{t("shell.branchSwitcher.fetching")}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!canSelectBranches) {
    const tokenBranchId = identity?.payload?.branch_id;

    if (selectedBranch) {
      return (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <Building2 className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {selectedBranch.name}
                </span>
                <span className="truncate text-xs">{t("shell.branchSwitcher.assignedBranch")}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      );
    }

    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Building2 className="size-4 animate-pulse" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{t("shell.branchSwitcher.loadingBranch")}</span>
              <span className="truncate text-xs">{t("shell.branchSwitcher.branchId", { id: tokenBranchId ?? "" })}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Building2 className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{t("shell.branchSwitcher.noBranches")}</span>
              <span className="truncate text-xs">{t("shell.branchSwitcher.noneAvailable")}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <Building2 className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{summaryTitle}</span>
                <span className="truncate text-xs">{summaryDescription}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-w-80 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {searchQuery.trim()
                ? t("shell.branchSwitcher.listLabelFiltered", {
                    shown: filteredBranches.length,
                    total: branches.length,
                  })
                : t("shell.branchSwitcher.listLabel", { count: branches.length })}
            </DropdownMenuLabel>

            {branches.length > 5 && (
              <>
                <div className="px-2 py-1">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      ref={searchInputRef}
                      placeholder={t("shell.branchSwitcher.searchPlaceholder")}
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setSelectedIndex(-1);
                      }}
                      className="h-8 pl-8 text-sm"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          setSearchQuery("");
                          setIsDropdownOpen(false);
                        } else if (e.key === "ArrowDown") {
                          e.preventDefault();
                          if (filteredBranches.length > 0) {
                            setSelectedIndex(0);
                          }
                        } else if (
                          e.key === "Enter" &&
                          filteredBranches.length > 0
                        ) {
                          e.preventDefault();
                          const branch =
                            selectedIndex >= 0
                              ? filteredBranches[selectedIndex]
                              : filteredBranches[0];
                          toggleBranch(branch.id);
                        }
                        e.stopPropagation();
                      }}
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            <div
              className="max-h-64 overflow-y-auto"
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setSelectedIndex((prev) =>
                    prev < filteredBranches.length - 1 ? prev + 1 : 0,
                  );
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setSelectedIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredBranches.length - 1,
                  );
                } else if (e.key === "Enter" && selectedIndex >= 0) {
                  e.preventDefault();
                  const branch = filteredBranches[selectedIndex];
                  if (branch) {
                    toggleBranch(branch.id);
                  }
                }
              }}
              tabIndex={-1}
            >
              {filteredBranches.length === 0 ? (
                <div className="p-2 text-center text-sm text-muted-foreground">
                  {searchQuery.trim()
                    ? t("shell.branchSwitcher.noMatch")
                    : t("shell.branchSwitcher.noBranches")}
                </div>
              ) : (
                filteredBranches.map((branch, index) => {
                  const isSelected = selectedBranchIds.includes(branch.id);

                  return (
                    <DropdownMenuItem
                      key={branch.id}
                      onSelect={(event) => {
                        event.preventDefault();
                        toggleBranch(branch.id);
                      }}
                      className={`gap-2 p-2 ${
                        index === selectedIndex
                          ? "bg-accent text-accent-foreground"
                          : ""
                      }`}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div className="flex size-6 items-center justify-center rounded-sm border">
                        {isSelected ? (
                          <Check className="size-4 text-primary" />
                        ) : (
                          <Building2 className="size-4 shrink-0" />
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium">
                          {branch.name}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  );
                })
              )}
            </div>

            <DropdownMenuSeparator />
            <div className="flex gap-1 px-2 py-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => selectBranches([])}
                disabled={selectedBranchIds.length === 0}
              >
                {t("shell.branchSwitcher.showAll")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => selectBranches(branches.map((branch) => branch.id))}
                disabled={selectedBranchIds.length === branches.length}
              >
                {t("shell.branchSwitcher.selectAll")}
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
